import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { getDashboard } from '@/api/dashboard';
import { listOrders } from '@/api/orders';
import {
  BarChart3,
  TrendingUp,
  Users,
  ClipboardList,
  Banknote,
  Clock,
} from 'lucide-react';

const statusLabels: Record<string, string> = {
  pending: '待开单',
  confirmed: '已确认',
  in_progress: '施工中',
  ready: '待交车',
  paid: '已收款',
  cancelled: '已取消',
};

export function ReportsPage() {
  const { data: dashboard, isLoading: dashboardLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: getDashboard,
  });

  const { data: allOrders } = useQuery({
    queryKey: ['orders', 'all'],
    queryFn: () => listOrders({ pageSize: 100 }),
  });

  // 统计各状态订单数量
  const statusCounts = allOrders?.items.reduce(
    (acc, o) => {
      acc[o.status] = (acc[o.status] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  ) ?? {};

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#1E293B]">经营报表</h1>
        <p className="mt-1 text-sm text-[#64748B]">
          门店经营数据概览与趋势分析
        </p>
      </div>

      {/* 核心指标 */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#EFF6FF]">
              <ClipboardList className="h-6 w-6 text-[#2563EB]" />
            </div>
            <div>
              <div className="text-2xl font-semibold text-[#1E293B]">
                {dashboard?.todayOrderCount ?? '-'}
              </div>
              <div className="text-sm text-[#64748B]">今日工单</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F0FDF4]">
              <Banknote className="h-6 w-6 text-[#16A34A]" />
            </div>
            <div>
              <div className="text-2xl font-semibold text-[#1E293B]">
                ¥{dashboard?.todayRevenue ?? '0'}
              </div>
              <div className="text-sm text-[#64748B]">今日营收</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#FFFBEB]">
              <Users className="h-6 w-6 text-[#D97706]" />
            </div>
            <div>
              <div className="text-2xl font-semibold text-[#1E293B]">
                {dashboard?.customerCount ?? '-'}
              </div>
              <div className="text-sm text-[#64748B]">客户总数</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#FEF2F2]">
              <Clock className="h-6 w-6 text-[#DC2626]" />
            </div>
            <div>
              <div className="text-2xl font-semibold text-[#1E293B]">
                ¥{dashboard?.unpaidTotal ?? '0'}
              </div>
              <div className="text-sm text-[#64748B]">待收款总额</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 订单状态分布 */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              订单状态分布
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardLoading ? (
              <div className="py-8 text-center text-sm text-[#94A3B8]">加载中...</div>
            ) : (
              <div className="space-y-3">
                {Object.entries(statusCounts).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between">
                    <span className="text-sm text-[#64748B]">
                      {statusLabels[status] || status}
                    </span>
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-24 overflow-hidden rounded-full bg-[#F1F5F9]">
                        <div
                          className="h-full rounded-full bg-[#2563EB]"
                          style={{
                            width: `${Math.min((count / (allOrders?.total || 1)) * 100, 100)}%`,
                          }}
                        />
                      </div>
                      <span className="w-8 text-right text-sm font-medium text-[#1E293B]">
                        {count}
                      </span>
                    </div>
                  </div>
                ))}
                {Object.keys(statusCounts).length === 0 && (
                  <p className="py-4 text-center text-sm text-[#94A3B8]">暂无订单数据</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              待办事项
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-lg bg-[#F8FAFC] p-4">
                <div>
                  <div className="font-medium text-[#1E293B]">施工中工单</div>
                  <div className="text-sm text-[#64748B]">正在维修的车辆</div>
                </div>
                <div className="text-2xl font-semibold text-[#2563EB]">
                  {dashboard?.inProgressCount ?? 0}
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-[#F8FAFC] p-4">
                <div>
                  <div className="font-medium text-[#1E293B]">待交车工单</div>
                  <div className="text-sm text-[#64748B]">已完成待交车</div>
                </div>
                <div className="text-2xl font-semibold text-[#16A34A]">
                  {dashboard?.readyForPickupCount ?? 0}
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-[#F8FAFC] p-4">
                <div>
                  <div className="font-medium text-[#1E293B]">待收款工单</div>
                  <div className="text-sm text-[#64748B]">已完工未收款</div>
                </div>
                <div className="text-2xl font-semibold text-[#D97706]">
                  {dashboard?.pendingPaymentCount ?? 0}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
